// SampleQuotation.js
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PDFDownloadLink } from "@react-pdf/renderer";
import InvoicePDF from "./SamplePDF";
import { sampleLeads } from "./SampleLead";

const sampleQuotations = [
  {
    id: 1,
    lead_id: 1,
    unit: "IT Department",
    pr_no: "PR-1001",
    pr_date: "2025-09-01",
    legacy_code: "LC-001",
    item_code: "ITM-1001",
    item_description: "Dell Latitude 5420 Laptop, Intel i5, 16GB RAM, 512GB SSD",
    uom: "Piece",
    pr_quantity: 5,
  },
  {
    id: 2,
    lead_id: 1,
    unit: "IT Department",
    pr_no: "PR-1002",
    pr_date: "2025-09-01",
    legacy_code: "LC-002",
    item_code: "ITM-1002",
    item_description: "HP LaserJet Pro M404dn Printer",
    uom: "Piece",
    pr_quantity: 2,
  },
  {
    id: 3,
    lead_id: 2,
    unit: "Marketing",
    pr_no: "PR-2001",
    pr_date: "2025-09-02",
    legacy_code: "LC-003",
    item_code: "ITM-2001",
    item_description: "Apple iPad Air 5th Gen, 64GB, Wi-Fi",
    uom: "Piece",
    pr_quantity: 3,
  },
  {
    id: 4,
    lead_id: 3,
    unit: "Operations",
    pr_no: "PR-3001",
    pr_date: "2025-09-02",
    legacy_code: "LC-004",
    item_code: "ITM-3001",
    item_description: "Canon EOS 90D DSLR Camera with 18-135mm Lens",
    uom: "Set",
    pr_quantity: 1,
  },
  {
    id: 5,
    lead_id: 4,
    unit: "HR Department",
    pr_no: "PR-4001",
    pr_date: "2025-09-03",
    legacy_code: "LC-005",
    item_code: "ITM-4001",
    item_description: "Ergonomic Office Chairs with lumbar support",
    uom: "Piece",
    pr_quantity: 20,
  },
];

const SampleQuotation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { leadId } = location.state || {};

  // Get lead information
  const leadInfo = sampleLeads.find(lead => lead.id === leadId) || {};

  // Filter quotations linked to this lead
  const filteredQuotations = sampleQuotations.filter(
    (q) => q.lead_id === leadId
  );

  const [isSending, setIsSending] = useState(false);
  const [quotationNumber, setQuotationNumber] = useState("");
  const [quotationDate, setQuotationDate] = useState("");
  const [quotationData, setQuotationData] = useState(
    filteredQuotations.map((q) => ({
      ...q,
      pricePerUnit: "",
      gstPrice: "",
    }))
  );

  // Handle input change
  const handleChange = (id, field, value) => {
    setQuotationData((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  // Calculate total summary
  const calculateSummary = () => {
    let subtotal = 0;
    let totalGST = 0;
    let grandTotal = 0;

    quotationData.forEach((item) => {
      const price = parseFloat(item.pricePerUnit) || 0;
      const gst = parseFloat(item.gstPrice) || 0;
      const qty = parseInt(item.pr_quantity) || 0;

      subtotal += qty * price;
      totalGST += gst;
    });

    grandTotal = subtotal + totalGST;
    return { subtotal, totalGST, grandTotal };
  };

  const { subtotal, totalGST, grandTotal } = calculateSummary();

  // Save Quotation
  const handleSave = () => {
    console.log("Quotation Saved:", {
      quotationNumber,
      quotationDate,
      quotationData,
      subtotal,
      totalGST,
      grandTotal,
    });
    alert("Quotation saved successfully!");
  };

  // Send Quotation
  const handleSendQuotation = () => {
    const today = new Date();
    const dateString = today.toISOString().split("T")[0];
    setQuotationDate(dateString);
    setQuotationNumber(`QUO${leadId.toString().padStart(3, '0')}`);
    setIsSending(true);
  };

  return (
    <div style={{ padding: "20px", position: "relative" }}>
      {/* Back button at left top */}
      <button
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: "20px",
          left: "20px",
          backgroundColor: "#007bff",
          color: "white",
          padding: "8px 16px",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
        }}
      >
        Back
      </button>

      {/* Centered title */}
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <h2>Quotation</h2>
        {isSending && (
          <div>
            <h3 style={{ margin: "5px 0" }}>Quotation Number: {quotationNumber}</h3>
          </div>
        )}
      </div>

      {leadId ? (
        filteredQuotations.length > 0 ? (
          <>
            {/* Date at right top */}
            {isSending && (
              <div style={{ textAlign: "right", marginBottom: "10px" }}>
                <strong>Date: {quotationDate}</strong>
              </div>
            )}

            <div style={{ display: "flex", gap: "20px" }}>
              {/* Table on the left */}
              <div style={{ flex: "3" }}>
                <table
                  style={{
                    borderCollapse: "collapse",
                    width: "100%",
                    border: "1px solid #ddd",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#f2f2f2" }}>
                      <th style={styles.th}>PR No</th>
                      <th style={styles.th}>PR Date</th>
                      <th style={styles.th}>Unit</th>
                      <th style={styles.th}>Item Code</th>
                      <th style={styles.th}>Item Description</th>
                      <th style={styles.th}>UOM</th>
                      <th style={styles.th}>Quantity</th>
                      {isSending && (
                        <>
                          <th style={styles.th}>Price/Unit (₹)</th>
                          <th style={styles.th}>GST (₹)</th>
                          <th style={styles.th}>Total (₹)</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {quotationData.map((q) => {
                      const total =
                        (parseFloat(q.pricePerUnit) || 0) *
                          (parseInt(q.pr_quantity) || 0) +
                        (parseFloat(q.gstPrice) || 0);
                      return (
                        <tr key={q.id}>
                          <td style={styles.td}>{q.pr_no}</td>
                          <td style={styles.td}>{q.pr_date}</td>
                          <td style={styles.td}>{q.unit}</td>
                          <td style={styles.td}>{q.item_code}</td>
                          <td style={styles.td}>{q.item_description}</td>
                          <td style={styles.td}>{q.uom}</td>
                          <td style={styles.td}>{q.pr_quantity}</td>
                          {isSending && (
                            <>
                              <td style={styles.td}>
                                <input
                                  type="number"
                                  value={q.pricePerUnit}
                                  onChange={(e) =>
                                    handleChange(q.id, "pricePerUnit", e.target.value)
                                  }
                                  style={{ width: "80px" }}
                                />
                              </td>
                              <td style={styles.td}>
                                <input
                                  type="number"
                                  value={q.gstPrice}
                                  onChange={(e) =>
                                    handleChange(q.id, "gstPrice", e.target.value)
                                  }
                                  style={{ width: "80px" }}
                                />
                              </td>
                              <td style={styles.td}>{total.toFixed(2)}</td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Summary and buttons on the right */}
              {isSending && (
                <div style={{ flex: "1", backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "5px", alignSelf: "flex-start" }}>
                  <h3 style={{ marginTop: 0, textAlign: "center" }}>Summary</h3>
                  <div style={styles.summaryItem}>
                    <span>Subtotal:</span>
                    <span>₹{subtotal.toFixed(2)}</span>
                  </div>
                  <div style={styles.summaryItem}>
                    <span>GST:</span>
                    <span>₹{totalGST.toFixed(2)}</span>
                  </div>
                  <div style={{ ...styles.summaryItem, fontWeight: "bold", borderTop: "1px solid #ddd", paddingTop: "10px" }}>
                    <span>Grand Total:</span>
                    <span>₹{grandTotal.toFixed(2)}</span>
                  </div>
                  
                  <div style={{ marginTop: "20px", display: "flex", flexDirection: "column", gap: "10px" }}>
                    <button onClick={handleSave} style={styles.actionButton}>
                      Save Quotation
                    </button>
                    
                    <PDFDownloadLink
                      document={
                        <InvoicePDF 
                          data={{
                            quotationNumber,
                            quotationDate,
                            subtotal,
                            totalGST,
                            grandTotal
                          }}
                          leadInfo={leadInfo}
                          quotationData={quotationData}
                        />
                      }
                      fileName={`quotation_${quotationNumber}.pdf`}
                      style={{
                        textDecoration: 'none',
                        padding: '8px 16px',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        borderRadius: '4px',
                        textAlign: 'center'
                      }}
                    >
                      {({ blob, url, loading, error }) =>
                        loading ? 'Loading document...' : 'Download Quotation'
                      }
                    </PDFDownloadLink>
                  </div>
                </div>
              )}
            </div>

            {!isSending && (
              <div style={{ textAlign: "center", marginTop: "20px" }}>
                <button
                  onClick={handleSendQuotation}
                  style={{
                    backgroundColor: "#28a745",
                    color: "white",
                    padding: "10px 20px",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "16px"
                  }}
                >
                  Send Quotation
                </button>
              </div>
            )}
          </>
        ) : (
          <p>No quotations found for this lead.</p>
        )
      ) : (
        <p>No lead selected.</p>
      )}
    </div>
  );
};

const styles = {
  th: {
    border: "1px solid #ddd",
    padding: "8px",
    textAlign: "left",
    fontSize: "12px"
  },
  td: {
    border: "1px solid #ddd",
    padding: "8px",
    fontSize: "12px"
  },
  actionButton: {
    backgroundColor: "#17a2b8",
    color: "white",
    padding: "10px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px"
  },
  summaryItem: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "8px",
    padding: "5px 0"
  }
};

export default SampleQuotation;